import { useMutation } from "@apollo/client";
import FileDownload from "@mui/icons-material/FileDownload";
import { Box, Button, Link, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import {
  CohortType,
  WaitForAllCohortReportQuery,
  WaitForAllCohortReportQueryVariables,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  EXPORT_WAIT_FOR_ALL_COHORT_REPORT_TO_CSV,
  WAIT_FOR_ALL_COHORT_REPORT_QUERY,
} from "./api";

type WaitForAllRow = WaitForAllCohortReportQuery["waitForAllCohortReport"]["items"][0];
const persistenceKey = "wait-for-all-cohort-report";

export function WaitForAllReport() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { getOmnisearchValue } = useOmnisearchInput({
    isURLDriven: true,
    initialFilters: [],
  });

  const reportingYear = useMemo(() => {
    const value = getOmnisearchValue("reportingYear");
    return safeParseInt(value ?? "") ?? currentTierIIReportingYear;
  }, [getOmnisearchValue]);

  const [exportCsv, { data: exportData, loading: exportLoading }] = useMutation(
    EXPORT_WAIT_FOR_ALL_COHORT_REPORT_TO_CSV
  );

  const handleExportCsv = useCallback(() => {
    exportCsv({
      variables: {
        reportingYear,
      },
    });
  }, [exportCsv, reportingYear]);

  const exportResult = exportData?.exportWaitForAllCohortReportToCsv;

  useEffect(() => {
    if (exportResult?.success) {
      alerts.success("Successfully generated the wait-for-all cohort export.");
    } else if (exportResult?.errorMsg) {
      alerts.error(
        `There was a problem generating the wait-for-all cohort export: ${exportResult.errorMsg}`
      );
    }
  }, [alerts, exportResult?.errorMsg, exportResult?.success]);

  useEffect(() => {
    if (exportResult?.downloadUrl) {
      window.open(exportResult.downloadUrl, "_blank");
    }
  }, [exportResult?.downloadUrl]);

  const columns = useMemo(
    (): Array<OmnisearchGridColDef<WaitForAllRow>> => [
      {
        field: "cohortName",
        headerName: "Cohort",
        filterKeyType: "text",
        flex: 1.6,
        minWidth: 260,
      },
      {
        field: "organizationName",
        headerName: "Organization",
        filterKeyType: "text",
        flex: 1.2,
        minWidth: 200,
      },
      {
        field: "state",
        headerName: "State",
        flex: 0.5,
        minWidth: 80,
      },
      {
        field: "cohortType",
        headerName: "Type",
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(CohortType),
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => prettyPrintEnumValue(row.cohortType),
      },
      {
        field: "cohortSize",
        headerName: "Cohort Size",
        filterKeyType: "number",
        type: "number",
        flex: 0.6,
        minWidth: 120,
      },
      {
        field: "readyCount",
        headerName: "Ready Count",
        filterKeyType: "number",
        type: "number",
        flex: 0.6,
        minWidth: 120,
      },
      {
        field: "waitingOnCount",
        headerName: "Waiting On Count",
        filterKeyType: "number",
        type: "number",
        flex: 0.7,
        minWidth: 150,
      },
      {
        field: "cohortLink",
        headerName: "Cohort Link",
        filterKeyType: "text",
        minWidth: 120,
        linkable: false,
        renderCell: ({ row }) => (
          <Link component={RouterLink} to={row.cohortLink}>
            Open Cohort
          </Link>
        ),
      },
    ],
    []
  );

  return (
    <Box p={theme.spacing(2)}>
      <Paper
        elevation={0}
        sx={{ display: "flex", flexDirection: "column", position: "relative" }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={3}
        >
          <Typography variant="body1" color={theme.palette.text.secondary}>
            Open cohorts still waiting for all members to be ready
          </Typography>
          <Button
            size="medium"
            loading={exportLoading}
            startIcon={<FileDownload />}
            onClick={handleExportCsv}
          >
            Export CSV
          </Button>
        </Stack>

        <Box pb={3} sx={{ height: "100vh" }}>
          <OmnisearchDataGrid<
            WaitForAllCohortReportQuery,
            WaitForAllCohortReportQueryVariables,
            WaitForAllRow
          >
            isURLDriven
            persistenceKey={persistenceKey}
            columns={columns}
            dataQuery={WAIT_FOR_ALL_COHORT_REPORT_QUERY}
            dataQueryVariables={{
              reportingYear,
            }}
            getItems={(data) => data.waitForAllCohortReport.items}
            getCount={(data) => data.waitForAllCohortReport.count}
            getRowId={(row) => row.cohortId}
            getRowLink={(row) => row.cohortLink}
            initialSortModel={[{ field: "cohortSize", sort: "desc" }]}
            initialPageSize={25}
            pageSizeOptions={[25, 50, 100]}
            noDataMessage={`No wait-for-all cohorts found for ${reportingYear}.`}
            gridSx={{
              height: "100%",
              minHeight: "400px",
              maxHeight: "calc(100vh - 280px)",
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
